import { Component, OnDestroy, OnInit } from '@angular/core';
import { from, of, Subject } from 'rxjs';
import { mergeMap, tap, catchError, take, takeUntil } from 'rxjs/operators';
import { ImportsCSVFileService } from 'src/shared/services/imports-CSV.service';
import { ServiceOrderService } from 'src/shared/services/service-order.service';
import { MapjsService } from 'src/shared/services/mapjs.service';

// Imports Standalone
import { PageHeaderComponent } from 'src/shared/components/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FileUploadModule } from 'primeng/fileupload';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DividerModule } from 'primeng/divider';
import { ProgressBarModule } from 'primeng/progressbar';

interface AppUser {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
}

interface ServiceOrderRow {
  rowIndex: number;
  osId: string;
  installationReference: string;
  defeitosInformados: string;
  observacaoCC: string;
  priority: string;
  assigneeUser: AppUser | null;
  installationId: string | null;
  latitude: number | null;
  longitude: number | null;
  deviceMac: string | null;
  status: 'pending' | 'loading' | 'valid' | 'invalid' | 'creating' | 'created' | 'error';
  errorMessage?: string;
}

@Component({
  selector: 'app-imports-csv-create-service-order',
  templateUrl: './imports-csv-create-service-order.component.html',
  styleUrl: './imports-csv-create-service-order.component.less',
  imports: [
    CommonModule,
    PageHeaderComponent,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    MatTooltip,
    DialogModule,
    AutoCompleteModule,
    DividerModule,
    ButtonModule,
    CardModule,
    FileUploadModule,
    ProgressBarModule,
    TagModule,
    TranslateModule,
  ]
})

export class ImportsCsvCreateServiceOrderComponent implements OnInit, OnDestroy {

  constructor(
    private importsCSVFileService: ImportsCSVFileService,
    private serviceOrderService: ServiceOrderService,
    private mapjsService: MapjsService,
  ) { }

  public company = localStorage.getItem('lastCompanySelected') || null;

  // Propriedade que manipula quando o modal de instruções será aberto
  public visibleInstructions: boolean = false;

  // Propriedade que srmazena o arquivo
  public file: File | null = null;

  // Dois tetos, porque os arquivos são diferentes. O carregado é a versão cheia (o Relatório
  // de Ordens tem ~50 colunas e o import usa 5), então aceita mais; o montado é o que sobe para
  // o alerts-api e precisa caber no limite de lá, que é o que o serviço comporta em memória.
  // Medido: 72,6 KB de entrada com 130 OS viram 13,1 KB enviados, ou 103 B por OS.
  private static readonly MAX_UPLOAD_BYTES = 10 * 1024 * 1024;  // arquivo carregado
  private static readonly MAX_BATCH_BYTES = 5 * 1024 * 1024;    // CSV montado, igual ao alerts-api

  // Propriedade que armazena a mensagem exibida após o upload do arquivo
  public messageUpload: string = '';

  // Propriedade que manipula o tipo de severidade exibida nas mensagens de sucesso/erro
  public messageSeverity: any;

  // CSV model detection
  public csvModel: 'editable' | null = null;

  // Editable rows state
  public serviceOrderRows: ServiceOrderRow[] = [];
  public selectedRows: ServiceOrderRow[] = [];
  public deletedRows: ServiceOrderRow[] = [];
  // Último batch removido (1 linha em delete individual, N em delete em lote). Suporta undo.
  public lastDeletedBatch: ServiceOrderRow[] = [];
  public isValidating: boolean = false;
  public validatedCount: number = 0;
  public totalToValidate: number = 0;
  public isCreating: boolean = false;
  // Snapshots dos contadores de valid/invalid no momento do início da criação.
  // Mantêm o footer estável (não "consome" o número conforme linhas viram created/error).
  // null = sem batch em andamento, exibe contagem dinâmica normal.
  public validCountSnapshot: number | null = null;
  private invalidCountSnapshot: number | null = null;
  private cancelValidation$ = new Subject<void>();
  // Subject disparado no ngOnDestroy. Combinado com takeUntil em todos os subscribes
  // pra evitar leaks/mutations órfãs quando o componente é destruído durante operações.
  private destroy$ = new Subject<void>();

  // Bulk edit
  public bulkPriority: string = '';
  public bulkUser: AppUser | null = null;
  public filteredUsers: AppUser[] = [];

  private allUsers: AppUser[] = [];

  // Mínimo alinhado ao autocomplete do formulário manual de OS. Era 5 (número de poste da MNLUZ)
  // e barrava referência curta antes de consultar o Geoserver, que é quem decide se existe.
  private readonly MIN_INSTALLATION_REF_LENGTH = 1;

  // Batch do lookup via Geoserver: nº de referências por request (limite de tamanho da URL/CQL)
  // e quantos chunks em paralelo.
  private readonly GEOSERVER_REF_CHUNK_SIZE = 150;
  private readonly GEOSERVER_CHUNK_CONCURRENCY = 3;

  // Cabeçalho do CSV modelo e dos exports humanos (mesmo formato do export 'lista-formatada'
  // → re-importável pela tela).
  // Sem acentuação: o backend do batch (service_order_batch_create) exige cabeçalho sem acento.
  public readonly templateHeaders = ['id_os', 'instalacao', 'defeitos_informados', 'observacao', 'prioridade', 'responsavel'];

  // Cabeçalho do CSV de envio ao batch: as 6 do modelo + 3 colunas derivadas do lookup no
  // Geoserver. Ficam fora do modelo humano de propósito — o usuário não preenche isso no Excel.
  // Precisa ser declarado depois de templateHeaders: campos de instância inicializam na ordem
  // de declaração, e o spread de undefined estouraria na construção do componente.
  private readonly batchHeaders = [...this.templateHeaders, 'instalacao_referencia', 'latitude', 'longitude', 'device_mac'];

  ngOnInit(): void {
    this.loadCompanyUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Lista completa dos usuários da empresa, carregada uma vez na abertura. A fonte antiga
  // (ManageUserService.getAllUsers) pedia só a primeira página do graphene (100): empresa com mais
  // usuários perdia o resto no autocomplete e na coluna do CSV.
  private loadCompanyUsers(): void {
    this.serviceOrderService.getAllCompanyUsers(this.company)
      .pipe(takeUntil(this.destroy$))
      .subscribe(users => {
        this.allUsers = users.map(u => ({
          id: u.id ?? '',
          username: u.username ?? '',
          firstName: u.firstName ?? '',
          lastName: u.lastName ?? '',
        }));
      });
  }
  // Método que lê o arquivo
  public async getFile(event: any): Promise<void> {
    const uploadedFile = event.files[0];
    this.onFileClear();

    // Checado antes do .text(): sem isto o arquivo inteiro é carregado em memória só para
    // descobrir que é grande demais.
    const maxUpload = ImportsCsvCreateServiceOrderComponent.MAX_UPLOAD_BYTES;
    if (uploadedFile && uploadedFile.size > maxUpload) {
      this.file = null;
      this.csvModel = null;
      this.messageUpload = `Arquivo de ${this.formatBytes(uploadedFile.size)} acima do limite de `
        + `${this.formatBytes(maxUpload)}. Divida a importação em arquivos menores.`;
      this.messageSeverity = 'danger';
      return;
    }

    this.file = uploadedFile;

    const text: string = await this.file!.text();
    // Remove BOM UTF-8 (U+FEFF) que Excel/OpenOffice prependam, senão o startsWith falha.
    const firstLine = text.split('\n')[0].replace(/^﻿/, '').trimStart();

    if (firstLine.startsWith('MANAUS LUZ')) {
      this.csvModel = 'editable';
      this.messageUpload = 'Modelo Manaus Luz detectado. Inicie a verificação para validar as instalações.';
      this.messageSeverity = 'info';
      this.parseRawCsv(text);
    } else if (/^id_os[;,\t]/.test(firstLine)) {
      // OpenOffice/Excel exportam com separador variável (; , ou tab): usa o char após 'id_os'.
      const separator = firstLine.charAt('id_os'.length);
      this.csvModel = 'editable';
      this.messageUpload = 'CSV formatado detectado. Revise os dados e inicie a verificação.';
      this.messageSeverity = 'info';
      this.parseFormattedCsv(text, separator);
    } else {
      // Fluxo "default" (upload cru) foi descontinuado: agora todo CSV passa pelo editável.
      this.csvModel = null;
      this.messageUpload = 'Formato de CSV não reconhecido. Baixe o modelo de exemplo e utilize esse formato.';
      this.messageSeverity = 'danger';
    }
  }

  public onFileClear(): void {
    this.messageUpload = '';
    this.csvModel = null;
    this.serviceOrderRows = [];
    this.selectedRows = [];
    this.deletedRows = [];
    this.bulkPriority = '';
    this.bulkUser = null;
    this.lastDeletedBatch = [];
    this.isValidating = false;
    this.validatedCount = 0;
    this.totalToValidate = 0;
    this.isCreating = false;
    this.validCountSnapshot = null;
    this.invalidCountSnapshot = null;
    // Cancela validação em andamento. Não recria o Subject pois reset() não é necessário —
    // takeUntil já desinscreveu, próximo subscribe usa o mesmo Subject.
    this.cancelValidation$.next();
  }

  // Full document-level RFC 4180 parser — handles multi-line quoted fields
  private parseCSV(text: string, separator: string = ','): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let current = '';
    let inQuotes = false;
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    for (let i = 0; i < normalized.length; i++) {
      const ch = normalized[i];
      if (ch === '"') {
        if (inQuotes && normalized[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === separator && !inQuotes) {
        row.push(current.trim()); current = '';
      } else if (ch === '\n' && !inQuotes) {
        row.push(current.trim()); current = '';
        if (row.some(v => v !== '')) rows.push(row);
        row = [];
      } else {
        current += ch;
      }
    }
    row.push(current.trim());
    if (row.some(v => v !== '')) rows.push(row);
    return rows;
  }

  private mapPriority(value: string | undefined): string {
    if (!value) return 'MEDIUM';
    const v = value.toUpperCase().trim();
    if (v === 'ALTA' || v === 'URGENTE') return 'HIGH';
    if (v === 'BAIXA') return 'LOW';
    return 'MEDIUM';
  }

  private parseRawCsv(csvText: string): void {
    const parsed = this.parseCSV(csvText);
    // parsed[0] = title row, parsed[1] = headers, parsed[2+] = data
    if (parsed.length < 3) return;

    const headers: string[] = parsed[1];
    const idIdx = headers.findIndex((h: string) => h.trim() === 'ID');
    const installationIdx = headers.findIndex((h: string) => h.trim() === 'Poste Nº');
    const prioIdx = headers.findIndex((h: string) => h.trim() === 'Prioridade');
    const defeitosIdx = headers.findIndex((h: string) => h.trim() === 'Defeitos informados');
    const obsIdx = headers.findIndex((h: string) => h.trim() === 'Observação - Criação Call Center');

    const rows: ServiceOrderRow[] = [];
    for (let i = 2; i < parsed.length; i++) {
      const values: string[] = parsed[i];
      const osId = values[idIdx]?.trim() || '';
      const installationRef = values[installationIdx]?.trim() || '';
      if (!osId && !installationRef) continue;
      // Sem referência → marca invalid no parse (linha não tem como ser validada).
      const refMissing = !installationRef;
      rows.push({
        rowIndex: i - 2,
        osId,
        installationReference: installationRef,
        defeitosInformados: values[defeitosIdx]?.trim() || '',
        observacaoCC: values[obsIdx]?.trim() || '',
        priority: this.mapPriority(values[prioIdx]),
        assigneeUser: null,
        installationId: null,
        latitude: null,
        longitude: null,
        deviceMac: null,
        status: refMissing ? 'invalid' : 'pending',
        errorMessage: refMissing ? 'Referência da instalação não informada' : undefined,
      });
    }

    this.serviceOrderRows = rows;
  }

  private parseFormattedCsv(csvText: string, separator: string = ';'): void {
    const parsed = this.parseCSV(csvText, separator);
    // parsed[0] = headers: id_os;instalação;defeitos_informados;observação;prioridade;responsavel
    if (parsed.length < 2) return;

    const rows: ServiceOrderRow[] = [];
    for (let i = 1; i < parsed.length; i++) {
      const values = parsed[i];
      const osId = values[0]?.trim() || '';
      const installationRef = values[1]?.trim() || '';
      if (!osId && !installationRef) continue;
      const matchedUser = this.matchCsvAssignee(values[5]?.trim() || '');
      // Sem referência → marca invalid no parse (linha não tem como ser validada).
      const refMissing = !installationRef;
      rows.push({
        rowIndex: i - 1,
        osId,
        installationReference: installationRef,
        defeitosInformados: values[2]?.trim() || '',
        observacaoCC: values[3]?.trim() || '',
        // mapPriority e não o valor cru: o modelo baixado é preenchido no Excel, e o backend só
        // aceita HIGH/MEDIUM/LOW — um "Alta" digitado pelo usuário invalidaria a linha.
        priority: this.mapPriority(values[4]),
        assigneeUser: matchedUser,
        installationId: null,
        latitude: null,
        longitude: null,
        deviceMac: null,
        status: refMissing ? 'invalid' : 'pending',
        errorMessage: refMissing ? 'Referência da instalação não informada' : undefined,
      });
    }
    this.serviceOrderRows = rows;
  }

  private lookupAllInstallations(rows: ServiceOrderRow[]): void {
    this.isValidating = true;
    this.validatedCount = 0;
    this.totalToValidate = rows.length;
    // Geoserver casa em lote (reference IN (...)), evitando 1 request por linha.
    this.lookupAllViaGeoserver(rows);
  }

  // Lookup em lote via Geoserver: agrupa as referências em chunks e faz 1 request
  // por chunk (reference IN (...)), aplicando o resultado a todas as linhas de cada ref.
  private lookupAllViaGeoserver(rows: ServiceOrderRow[]): void {
    // Sem empresa selecionada não há como consultar o Geoserver (evita atob(null) estourar).
    if (!this.company) {
      this.isValidating = false;
      return;
    }

    // Valida comprimento localmente; só consulta referências aptas.
    const queryable: ServiceOrderRow[] = [];
    rows.forEach(row => {
      const ref = row.installationReference?.trim() ?? '';
      if (ref.length < this.MIN_INSTALLATION_REF_LENGTH) {
        row.status = 'invalid';
        row.errorMessage = `Referência da instalação deve ter ao menos ${this.MIN_INSTALLATION_REF_LENGTH} caracteres`;
        this.validatedCount++;
      } else {
        row.status = 'loading';
        queryable.push(row);
      }
    });

    if (queryable.length === 0) {
      this.isValidating = false;
      return;
    }

    // Índice referência -> linhas (várias linhas podem compartilhar a mesma referência).
    const rowsByRef = new Map<string, ServiceOrderRow[]>();
    queryable.forEach(r => {
      const key = r.installationReference.trim();
      const list = rowsByRef.get(key) ?? [];
      list.push(r);
      rowsByRef.set(key, list);
    });

    // Referências únicas divididas em chunks (limite de tamanho da URL do Geoserver).
    const uniqueRefs = Array.from(rowsByRef.keys());
    const chunks: string[][] = [];
    for (let i = 0; i < uniqueRefs.length; i += this.GEOSERVER_REF_CHUNK_SIZE) {
      chunks.push(uniqueRefs.slice(i, i + this.GEOSERVER_REF_CHUNK_SIZE));
    }

    const company = window.atob(this.company!);

    from(chunks).pipe(
      mergeMap(chunk => {
        // Match case-insensitive (lowercase). O "&sortBy=reference" vira param WFS separado:
        // map_nodes (dev) não tem PK e o geoserver exige ordenação explícita p/ avaliar IN.
        const inList = chunk.map(ref => this.cqlQuoteLower(ref)).join(',');
        // Sem filtro is_active: instalação inativa também pode ser elencada para manutenção.
        const cql = `strToLowerCase(reference) IN (${inList})&sortBy=reference`;
        return this.mapjsService.filterByElements(cql, company).pipe(
          take(1),
          tap((res: any) => {
            const features: any[] = res?.features ?? [];
            const featureByRef = new Map<string, any>();
            features.forEach(f => {
              const fref = f?.properties?.reference;
              if (fref == null) return;
              const key = String(fref).toLowerCase();
              const current = featureByRef.get(key);
              // Sem o filtro is_active no CQL a mesma referência pode voltar mais de uma vez
              // (registro ativo + inativo). Prefere a ativa; a inativa fica como fallback.
              if (!current || (!this.isActiveFeature(current) && this.isActiveFeature(f))) {
                featureByRef.set(key, f);
              }
            });
            chunk.forEach(ref => this.applyGeoserverFeatureToRows(
              rowsByRef.get(ref) ?? [],
              featureByRef.get(ref.toLowerCase()),
            ));
          }),
          catchError(() => {
            // Chunk falhou: marca as linhas do chunk como invalid (sem match).
            chunk.forEach(ref => this.applyGeoserverFeatureToRows(rowsByRef.get(ref) ?? [], null));
            return of(null);
          }),
        );
      }, this.GEOSERVER_CHUNK_CONCURRENCY),
      takeUntil(this.cancelValidation$),
      takeUntil(this.destroy$),
    ).subscribe({
      complete: () => { this.isValidating = false; }
    });
  }

  // map_nodes pode devolver is_active como boolean ou como string ('true'/'false').
  // Usado só pra desempatar referências duplicadas, não pra invalidar linha.
  private isActiveFeature(feature: any): boolean {
    const value = feature?.properties?.is_active;
    return value === true || String(value).toLowerCase() === 'true';
  }

  // Normaliza uma feature do Geoserver para o formato agnóstico do applyInstallationToRow.
  // Retorna null quando não houve match.
  private geoserverFeatureToFound(feature: any) {
    const props = feature?.properties;
    if (!props) return null;
    const coords = feature?.geometry?.coordinates;
    return {
      id: props?.id != null ? String(props.id) : null,
      latitude: coords?.[1] ?? null,
      longitude: coords?.[0] ?? null,
      // map_nodes usa a string 'NULL' quando a instalação não tem equipamento associado.
      // Enviar isso como MAC gravaria lixo, então normaliza para null.
      deviceMac: props?.device_mac && String(props.device_mac).toUpperCase() !== 'NULL'
        ? String(props.device_mac)
        : null,
    };
  }

  // Aplica uma feature do Geoserver a todas as linhas da referência, incrementando o progresso.
  private applyGeoserverFeatureToRows(rows: ServiceOrderRow[], feature: any): void {
    const found = this.geoserverFeatureToFound(feature);
    rows.forEach(r => {
      this.applyInstallationToRow(r, found);
      this.validatedCount++;
    });
  }

  public startValidation(): void {
    const pending = this.serviceOrderRows.filter(r => r.status === 'pending');
    if (pending.length === 0) return;
    this.lookupAllInstallations(pending);
  }

  public cancelValidation(): void {
    this.cancelValidation$.next();
    this.serviceOrderRows
      .filter(r => r.status === 'loading')
      .forEach(r => { r.status = 'pending'; r.errorMessage = undefined; });
    this.isValidating = false;
  }

  public hasPendingRows(): boolean {
    return this.serviceOrderRows.some(r => r.status === 'pending');
  }

  public pendingRowsCount(): number {
    return this.serviceOrderRows.filter(r => r.status === 'pending').length;
  }

  private lookupSingleInstallation(row: ServiceOrderRow) {
    if (!row.installationReference?.trim() || row.installationReference.trim().length < this.MIN_INSTALLATION_REF_LENGTH) {
      row.status = 'invalid';
      row.errorMessage = `Referência da instalação deve ter ao menos ${this.MIN_INSTALLATION_REF_LENGTH} caracteres`;
      return of(null);
    }
    row.status = 'loading';
    return this.lookupViaGeoserver(row);
  }

  // Aplica os dados da instalação encontrada na linha (ou marca invalid se não achou).
  // Fonte-agnóstico: recebe o resultado já normalizado de GraphQL ou Geoserver.
  // Basta a instalação existir. Duas regras que NÃO se aplicam a OS de manutenção:
  // - equipamento associado (MAC): a OS pode ser justamente pra solicitar a instalação dele;
  // - instalação ativa: uma instalação inativa também pode ser elencada para manutenção.
  private applyInstallationToRow(
    row: ServiceOrderRow,
    found: { id: string | null; latitude: number | null; longitude: number | null; deviceMac: string | null } | null,
  ): void {
    // found.id nulo = feature sem 'id' nas properties. Vale como não encontrada: sem installationId
    // a linha não tem como ser criada (o campo instalacao é obrigatório no backend).
    if (!found || found.id == null) {
      row.installationId = null;
      // Coordenadas também são zeradas: agora que são enviadas ao backend, não podem ficar
      // com o valor de um lookup anterior numa linha que voltou a ser inválida.
      row.latitude = null;
      row.longitude = null;
      row.deviceMac = null;
      row.status = 'invalid';
      row.errorMessage = undefined;
      return;
    }
    row.installationId = found.id;
    row.latitude = found.latitude;
    row.longitude = found.longitude;
    row.deviceMac = found.deviceMac;
    row.status = 'valid';
    row.errorMessage = undefined;
  }

  // Referência em lowercase entre aspas simples para o CQL (escapa aspas).
  private cqlQuoteLower(ref: string): string {
    return `'${ref.toLowerCase().replace(/'/g, "''")}'`;
  }

  // Relay global id base64 "Tipo:123" -> "123" (Installation/User/Company).
  private relayIdToNumeric(relayId: string | null | undefined): string | null {
    if (!relayId) return null;
    try {
      return atob(relayId).split(':')[1] ?? null;
    } catch {
      return null;
    }
  }

  // Consulta via Geoserver (map_nodes), mesmo endpoint do mapa (filterByElements).
  // Match case-insensitive de reference (igual ao cadastro/mapa).
  private lookupViaGeoserver(row: ServiceOrderRow) {
    // Sem empresa selecionada não há como consultar o Geoserver (evita atob(null) estourar).
    if (!this.company) {
      row.status = 'invalid';
      return of(null);
    }
    const ref = row.installationReference.trim();
    const refLower = ref.toLowerCase();
    // sortBy=reference anexado após o CQL — ver comentário no lookup em lote (camada sem PK no dev).
    // Sem filtro is_active: instalação inativa também pode ser elencada para manutenção.
    const cql = `strToLowerCase(reference)=${this.cqlQuoteLower(ref)}&sortBy=reference`;
    // companyId no localStorage é base64; filterByElements espera decodificado (igual ao mapa).
    return this.mapjsService
      .filterByElements(cql, window.atob(this.company))
      .pipe(
        take(1),
        tap((res: any) => {
          const features: any[] = res?.features ?? [];
          const matches = features.filter(
            (f: any) => String(f?.properties?.reference ?? '').toLowerCase() === refLower,
          );
          // Mesma referência pode voltar ativa e inativa; prefere a ativa, inativa é fallback.
          const match = matches.find((f: any) => this.isActiveFeature(f)) ?? matches[0];
          this.applyInstallationToRow(row, this.geoserverFeatureToFound(match));
        }),
        catchError(() => {
          row.status = 'invalid';
          return of(null);
        })
      );
  }

  // Re-valida linha após edição manual da referência (gatilho: blur do input).
  // Validação de comprimento já ocorre dentro de lookupSingleInstallation.
  public relookupRow(row: ServiceOrderRow): void {
    this.lookupSingleInstallation(row).pipe(takeUntil(this.destroy$)).subscribe();
  }

  // Busca por login ou nome, com termo composto: cada palavra digitada precisa casar em algum dos
  // três campos ("bete silva" acha Bete Silva; "bete souza" não). A lista está inteira em memória,
  // então isso é feito aqui, sem depender do `search` do bifrost, que só casa o termo inteiro.
  public filterUsers(event: any): void {
    const words = this.searchWords(event.query || '');
    this.filteredUsers = this.allUsers.filter((u: AppUser) => this.userMatchesWords(u, words));
  }

  // Coluna `responsavel` do CSV: login exato ou nome, comparado por palavras (sem acento/caixa)
  // nos dois sentidos, para o CSV poder ter mais ou menos sobrenomes que o cadastro. Só vale se
  // sobrar um único usuário; ambíguo fica em branco para o operador escolher no autocomplete.
  private matchCsvAssignee(value: string): AppUser | null {
    if (!value) return null;
    const byUsername = this.allUsers.find(u => u.username === value);
    if (byUsername) return byUsername;

    const csvWords = this.searchWords(value);
    if (csvWords.length === 0) return null;
    // 1) toda palavra do CSV está em algum campo do usuário (mesma regra do autocomplete)
    const csvInUser = this.allUsers.filter(u => this.userMatchesWords(u, csvWords));
    if (csvInUser.length === 1) return csvInUser[0];
    // 2) toda palavra do nome cadastrado está no CSV (cadastro mais curto que o CSV)
    const userInCsv = this.allUsers.filter(u => {
      const nameWords = this.searchWords(`${u.firstName} ${u.lastName}`);
      return nameWords.length > 0 && nameWords.every(w => csvWords.includes(w));
    });
    return userInCsv.length === 1 ? userInCsv[0] : null;
  }

  private searchWords(value: string): string[] {
    return this.normalizeForSearch(value).split(/\s+/).filter(w => w);
  }

  private userMatchesWords(u: AppUser, words: string[]): boolean {
    const fields = [u.username, u.firstName, u.lastName].map(f => this.normalizeForSearch(f));
    return words.every(w => fields.some(f => f.includes(w)));
  }

  // Minúsculas e sem acento nos dois lados: "antonio" acha "Antônio", "joao" acha "João".
  private normalizeForSearch(value: string): string {
    return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  public applyBulkEdit(): void {
    this.selectedRows.forEach(row => {
      if (this.bulkPriority) row.priority = this.bulkPriority;
      if (this.bulkUser) row.assigneeUser = this.bulkUser;
    });
    this.bulkPriority = '';
    this.bulkUser = null;
    this.selectedRows = [];
  }

  public deleteRow(row: ServiceOrderRow): void {
    this.removeRows([row]);
  }

  public deleteSelectedRows(): void {
    if (this.selectedRows.length === 0) return;
    this.removeRows(this.selectedRows);
  }

  private removeRows(rows: ServiceOrderRow[]): void {
    const indexes = new Set(rows.map(r => r.rowIndex));
    const snapshots = rows.map(r => ({ ...r }));
    this.deletedRows.push(...snapshots);
    this.serviceOrderRows = this.serviceOrderRows.filter(r => !indexes.has(r.rowIndex));
    this.selectedRows = this.selectedRows.filter(r => !indexes.has(r.rowIndex));
    this.lastDeletedBatch = snapshots;
  }

  public undoDelete(): void {
    if (this.lastDeletedBatch.length === 0) return;
    const restoredIndexes = new Set(this.lastDeletedBatch.map(r => r.rowIndex));
    this.deletedRows = this.deletedRows.filter(r => !restoredIndexes.has(r.rowIndex));
    this.serviceOrderRows = [...this.serviceOrderRows, ...this.lastDeletedBatch]
      .sort((a, b) => a.rowIndex - b.rowIndex);
    this.lastDeletedBatch = [];
  }

  // Helper que serializa uma linha para o formato CSV padrão (id_os;instalação;...).
  // Campo extra opcional vai no final (usado para motivo de erro nos downloads de inválidas/log).
  private rowToCsvFields(row: ServiceOrderRow, extra?: string): string {
    const fields = [
      row.osId,
      row.installationReference,
      this.escapeCsvField(row.defeitosInformados),
      this.escapeCsvField(row.observacaoCC),
      row.priority,
      row.assigneeUser?.username || '',
    ];
    if (extra !== undefined) fields.push(this.escapeCsvField(extra));
    return fields.join(';');
  }

  public downloadInvalidRows(): void {
    const headers = [...this.templateHeaders, 'erro'];
    const rows = this.serviceOrderRows
      .filter(r => r.status === 'invalid')
      .map(r => this.rowToCsvFields(r, r.errorMessage || 'Instalação não encontrada'));
    const content = headers.join(';') + '\n' + rows.join('\n');
    this.triggerDownload(content, `lista-instalacoes-invalidas_${this.shortUuid()}.csv`);
  }

  public getRowClass(row: ServiceOrderRow): string {
    if (row.status === 'invalid') return 'row-invalid';
    if (row.status === 'created') return 'row-created';
    if (row.status === 'error') return 'row-error';
    return '';
  }

  public getRowTooltip(row: ServiceOrderRow): string {
    if (row.status === 'invalid') {
      return row.errorMessage || 'Referência da instalação não encontrada no sistema';
    }
    if (row.status === 'error') {
      return row.errorMessage || 'Erro ao criar ordem de serviço';
    }
    return '';
  }

  // Permite criar se há linhas 'valid' aptas, sem estados intermediários
  // bloqueantes (pending/loading/invalid). 'created' e 'error' são finais e
  // não impedem retomar criação de linhas valid remanescentes (cenário típico
  // pós-cancelamento parcial).
  public canCreate(): boolean {
    const validRows = this.serviceOrderRows.filter(r => r.status === 'valid');
    if (validRows.length === 0) return false;
    const hasBlockingState = this.serviceOrderRows.some(
      r => r.status === 'pending' || r.status === 'loading' || r.status === 'invalid'
    );
    if (hasBlockingState) return false;
    return validRows.every(r => r.assigneeUser !== null && typeof r.assigneeUser === 'object');
  }

  public hasLoadingRows(): boolean {
    return this.serviceOrderRows.some(r => r.status === 'loading');
  }

  public validRowsCount(): number {
    return this.serviceOrderRows.filter(r => r.status === 'valid').length;
  }

  public invalidRowsCount(): number {
    return this.serviceOrderRows.filter(r => r.status === 'invalid').length;
  }

  // Counts exibidos no footer. Usam snapshot durante criação/pós-batch
  // pra não "consumir" o número conforme linhas mudam de status.
  // Fora desse modo, retornam contagem dinâmica normal.
  public displayedValidCount(): number {
    if (this.validCountSnapshot !== null && (this.isCreating || this.hasPartialResults())) {
      return this.validCountSnapshot;
    }
    return this.validRowsCount();
  }

  public displayedInvalidCount(): number {
    if (this.invalidCountSnapshot !== null && (this.isCreating || this.hasPartialResults())) {
      return this.invalidCountSnapshot;
    }
    return this.invalidRowsCount();
  }

  public discardInvalidRows(): void {
    const invalid = this.serviceOrderRows.filter(r => r.status === 'invalid');
    invalid.forEach(r => this.deletedRows.push({ ...r }));
    this.serviceOrderRows = this.serviceOrderRows.filter(r => r.status !== 'invalid');
    this.selectedRows = this.selectedRows.filter(r => r.status !== 'invalid');
  }

  public createdCount(): number {
    return this.serviceOrderRows.filter(r => r.status === 'created').length;
  }

  // Erro = só falha de criação (status 'error'). Inválidas/removidas não entram.
  public errorCount(): number {
    return this.serviceOrderRows.filter(r => r.status === 'error').length;
  }

  // Traduz a mensagem crua do batch (string REST do results[]) para algo amigável.
  private translateBatchError(msg: string): string {
    const m = (msg || '').toLowerCase();
    if (m.includes('service_order_company_reference_deleted_at_key') || m.includes('duplicate key')) {
      return 'ID da OS já existe no sistema';
    }
    return msg || 'Erro ao criar ordem de serviço';
  }

  public createAllServiceOrders(): void {
    const validRows = this.serviceOrderRows.filter(r => r.status === 'valid');
    this.submitBatchCsv(validRows);
  }

  // Monta o CSV das linhas válidas e envia num único POST ao batch.
  private submitBatchCsv(rows: ServiceOrderRow[]): void {
    if (this.isCreating) return;
    if (rows.length === 0) return;

    this.isCreating = true;
    this.validCountSnapshot = rows.length;
    this.invalidCountSnapshot = this.invalidRowsCount();
    rows.forEach(r => { r.status = 'creating'; });

    const csv = this.buildBatchCsv(rows);
    const file = new File(
      [ImportsCsvCreateServiceOrderComponent.UTF8_BOM + csv],
      `service-orders_${this.shortUuid()}.csv`,
      { type: 'text/csv;charset=utf-8;' },
    );

    // Medido no arquivo que realmente sobe, já com instalação, coordenadas e MAC preenchidos
    // pela validação — o tamanho do arquivo de entrada não serve de proxy.
    const maxBatch = ImportsCsvCreateServiceOrderComponent.MAX_BATCH_BYTES;
    if (file.size > maxBatch) {
      this.isCreating = false;
      this.validCountSnapshot = null;
      this.invalidCountSnapshot = null;
      rows.forEach(r => { r.status = 'valid'; });
      this.messageUpload = `O lote gerado tem ${this.formatBytes(file.size)}, acima do limite de `
        + `${this.formatBytes(maxBatch)}. Selecione menos linhas e envie em partes.`;
      this.messageSeverity = 'danger';
      return;
    }

    const company = this.company;

    this.importsCSVFileService.serviceOrderCreate(file, company)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isCreating = false;
          this.handleBatchResponse(res, rows);
        },
        error: (err: any) => {
          this.isCreating = false;
          // Falha de envio: nada foi criado. Volta para 'valid' p/ reenviar.
          rows.forEach(r => { r.status = 'valid'; });
          // 401/403 = sessão/token: mensagem específica em vez de "erro genérico".
          this.messageUpload = (err?.status === 401 || err?.status === 403)
            ? 'Sessão expirada ou sem permissão. Faça login novamente e tente de novo.'
            : 'Ocorreu um erro ao enviar o lote. Tente novamente.';
          this.messageSeverity = 'danger';
          console.error('Erro no batch service order create', err);
        },
      });
  }

  // O backend casa coluna por nome de cabeçalho e não aceita linha com contagem de campos
  // diferente do cabeçalho, então todo campo de texto livre precisa passar pelo escapeCsvField:
  // um ';' cru desloca as colunas seguintes e invalida a linha.
  // Nulos usam '??' e não '||': 0 é coordenada válida (equador/Greenwich).
  private buildBatchCsv(rows: ServiceOrderRow[]): string {
    const header = this.batchHeaders.join(';');
    const lines = rows.map(r => [
      this.escapeCsvField(r.osId),
      r.installationId ?? '',
      this.escapeCsvField(r.defeitosInformados),
      this.escapeCsvField(r.observacaoCC),
      r.priority,
      r.assigneeUser?.id ? (this.relayIdToNumeric(r.assigneeUser.id) ?? '') : '',
      this.escapeCsvField(r.installationReference),
      r.latitude ?? '',
      r.longitude ?? '',
      this.escapeCsvField(r.deviceMac ?? ''),
    ].join(';'));
    return header + '\n' + lines.join('\n');
  }

  // A API devolve totais {created, errors} + results[] por linha ({id_os, ok, message}).
  // Casa por posição: o backend empurra exatamente uma entrada em results[] por linha do CSV
  // (sucesso, erro de campo ou erro estrutural), na mesma ordem em que buildBatchCsv as escreveu.
  // O id_os não serve mais de chave: passou a ser opcional e, quando vazio, quem gera a
  // referência é o servidor — o front não tem esse valor no momento do envio.
  // Cardinalidade divergente (só acontece com '\n' cru sem aspas, que escapeCsvField evita):
  // cai no casamento por id_os antigo, que é conservador.
  // Sem results[] (API antiga / envelope de erro) → fallback só-totais (não marca por linha).
  private handleBatchResponse(res: any, rows: ServiceOrderRow[]): void {
    const results: any[] = Array.isArray(res?.results) ? res.results : [];

    if (results.length > 0) {
      if (results.length === rows.length) {
        results.forEach((r, i) => {
          const row = rows[i];
          if (r?.ok) {
            row.status = 'created';
            row.errorMessage = undefined;
            // Linha enviada sem ID OS: adota a referência gerada pelo servidor, que passa a
            // aparecer na coluna "ID OS" da tabela.
            if (!row.osId) row.osId = r?.id_os != null ? String(r.id_os) : '';
          } else {
            row.status = 'error';
            row.errorMessage = this.translateBatchError(r?.message ?? '');
          }
        });
        this.applyBatchTotalsMessage(rows);
        return;
      }

      const byId = new Map<string, { ok: boolean; message?: string }>();
      results.forEach(r => {
        const id = r?.id_os != null ? String(r.id_os) : '';
        if (id && !byId.has(id)) byId.set(id, { ok: !!r?.ok, message: r?.message });
      });

      rows.forEach(row => {
        const r = byId.get(row.osId);
        if (r?.ok) {
          row.status = 'created';
          row.errorMessage = undefined;
        } else {
          // Linha ok=false ou ausente do results[] (não deve ocorrer): trata como erro.
          row.status = 'error';
          row.errorMessage = this.translateBatchError(r?.message ?? '');
        }
      });

      this.applyBatchTotalsMessage(rows);
      return;
    }

    // Fallback sem results[]: só totais. Sem detalhe por linha não dá pra marcar quem falhou;
    // 100% → 'created', qualquer erro → volta 'valid' (reenviável) evitando marcar às cegas.
    const created = res?.created;
    const errors = res?.errors;
    if (typeof created === 'number' && typeof errors === 'number') {
      if (errors === 0) {
        rows.forEach(r => { r.status = 'created'; });
        this.messageUpload = `Foram criadas ${created} ordens de serviço com sucesso.`;
        this.messageSeverity = 'success';
      } else {
        rows.forEach(r => { r.status = 'valid'; });
        this.messageUpload = `Lote processado: ${created} criada(s), ${errors} com erro. Revise e tente novamente.`;
        this.messageSeverity = 'danger';
      }
      return;
    }

    // Shape inesperado / envelope de erro bifrost: não assume criação; reenviável.
    rows.forEach(r => { r.status = 'valid'; });
    const bifrostMsg = Array.isArray(res?.errors) ? res.errors[0]?.message : undefined;
    this.messageUpload = bifrostMsg
      ? `Não foi possível criar as ordens: ${bifrostMsg}`
      : `Não foi possível criar as ordens. Resposta do servidor: ${JSON.stringify(res)}`;
    this.messageSeverity = 'danger';
  }

  // Totais + mensagem do lote a partir do status já marcado em cada linha.
  private applyBatchTotalsMessage(rows: ServiceOrderRow[]): void {
    const createdN = rows.filter(r => r.status === 'created').length;
    const errorN = rows.filter(r => r.status === 'error').length;
    if (errorN === 0) {
      this.messageUpload = `Foram criadas ${createdN} ordens de serviço com sucesso.`;
      this.messageSeverity = 'success';
    } else if (createdN === 0) {
      this.messageUpload = `Nenhuma ordem criada. ${errorN} com erro. Revise e tente novamente.`;
      this.messageSeverity = 'danger';
    } else {
      this.messageUpload = `Lote processado: ${createdN} criada(s), ${errorN} com erro.`;
      this.messageSeverity = 'warning';
    }
  }

  // Retentativa em lote das linhas com erro: reenvia só elas ao batch.
  // canCreate não é checado: linhas error já passaram pelo lookup (têm installationId).
  public retryAllErrors(): void {
    const errorRows = this.serviceOrderRows.filter(r => r.status === 'error');
    if (errorRows.length === 0) return;
    errorRows.forEach(r => { r.errorMessage = undefined; });
    this.submitBatchCsv(errorRows);
  }

  public failedRowsCount(): number {
    return this.serviceOrderRows.filter(r => r.status === 'error').length;
  }

  // Linhas com resultado processado (created ou error). Usado pra habilitar download parcial.
  public hasPartialResults(): boolean {
    return this.serviceOrderRows.some(r => r.status === 'created' || r.status === 'error');
  }

  // Retenta uma única linha com erro: reenvia só ela ao batch.
  public retryRow(row: ServiceOrderRow): void {
    row.errorMessage = undefined;
    this.submitBatchCsv([row]);
  }

  public downloadSuccessCsv(): void {
    const headers = this.templateHeaders;
    const rows = this.serviceOrderRows
      .filter(r => r.status === 'created')
      .map(r => this.rowToCsvFields(r));
    const content = headers.join(';') + '\n' + rows.join('\n');
    this.triggerDownload(content, `lista-ordens-criadas_${this.shortUuid()}.csv`);
  }

  public downloadformatedCsv(): void {
    const headers = this.templateHeaders;
    const rows = this.serviceOrderRows.map(r => this.rowToCsvFields(r));
    const content = headers.join(';') + '\n' + rows.join('\n');
    this.triggerDownload(content, `lista-formatada_${this.shortUuid()}.csv`);
  }

  public downloadValidRows(): void {
    const headers = this.templateHeaders;
    const rows = this.serviceOrderRows
      .filter(r => r.status === 'valid' || r.status === 'created')
      .map(r => this.rowToCsvFields(r));
    const content = headers.join(';') + '\n' + rows.join('\n');
    this.triggerDownload(content, `lista-instalacoes-validas_${this.shortUuid()}.csv`);
  }

  // Só falhas de criação (status 'error'); removidas/inválidas não entram.
  public downloadErrorLog(): void {
    const headers = [...this.templateHeaders, 'erro'];
    const errorRows = this.serviceOrderRows
      .filter(r => r.status === 'error')
      .map(r => this.rowToCsvFields(r, r.errorMessage || 'Erro desconhecido'));
    const content = headers.join(';') + '\n' + errorRows.join('\n');
    this.triggerDownload(content, `log-erros_${this.shortUuid()}.csv`);
  }

  private escapeCsvField(value: string): string {
    if (!value) return '';
    if (value.includes(';') || value.includes('\n') || value.includes('"')) {
      return '"' + value.replace(/"/g, '""') + '"';
    }
    return value;
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  private shortUuid(): string {
    return crypto.randomUUID().split('-')[0];
  }

  // UTF-8 BOM (U+FEFF). Sem isto, Excel abre CSV como Latin-1 e quebra acentos (ex: "instalação" → "instalaÃ§Ã£o").
  private static readonly UTF8_BOM = '\uFEFF';

  private triggerDownload(content: string, fileName: string): void {
    const normalized = content.replace(/\r\n/g, '\n').replace(/\n/g, '\r\n');
    const blob = new Blob([ImportsCsvCreateServiceOrderComponent.UTF8_BOM + normalized], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Gera CSV modelo no formato exportado (lista-formatada): só o header, re-importável pela tela.
  public downloadCsvFile(): void {
    const csvContent = this.templateHeaders.join(';') + '\n';
    this.triggerDownload(csvContent, 'import-create-service-order.csv');
  }
}
